import React from "react";

// material-ui
import { useTheme } from "@mui/material/styles";
import {
  Chip,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import * as RouteHelper from "@utils/routes";

// project imports
import Avatar from "@stories/ui-component/extended/Avatar";

// assets
import {
  ContractorReportLineItem,
  ReportStatus,
} from "@interfaces/contractorReport";
import { ProjectLineItem } from "@interfaces/project";
import { StyledCard } from "@stories/atoms/StyledCard/StyledCard";
import { useNavigate } from "react-router-dom";
import { NoRows } from "../ListTable/NoRows";
import { DataLoadingRow } from "../ListTable/DataLoadingRow";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";

interface ReportTableProps {
  loading: boolean;
  project: ProjectLineItem;
  reports: ContractorReportLineItem[];
}

export const ReportTable = ({
  loading,
  project,
  reports,
}: ReportTableProps) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const handleRowClick = (reportId: string) => {
    navigate(RouteHelper.labourHours(project.id, reportId));
  };

  return (
    <StyledCard>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Sub Contractor Name</TableCell>
              <TableCell>Due Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell></TableCell>
              {/* <TableCell>Hours</TableCell>
              <TableCell>Spend</TableCell> */}
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading &&
              reports &&
              reports.map((row, index) => (
                <TableRow
                  hover
                  key={index}
                  onClick={() => handleRowClick(row.id)}
                >
                  <TableCell>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item>
                        <Avatar alt="User 1" src={row.organisationLogo} />
                      </Grid>
                      <Grid item xs zeroMinWidth>
                        <Typography
                          align="left"
                          variant="subtitle1"
                          component="div"
                        >
                          {row.organisationName}{" "}
                          {/* {row.status === "Active" && (
                          <CheckCircleIcon
                            sx={{
                              color: "success.dark",
                              width: 14,
                              height: 14,
                            }}
                          />
                        )} */}
                        </Typography>
                        <Typography align="left" variant="subtitle2" noWrap>
                          {`${row.created.toLocaleDateString()}`}
                        </Typography>
                      </Grid>
                    </Grid>
                  </TableCell>
                  <TableCell>
                    {`${project.reportingDueDate} / ${row.dueDateMonth + 1} / ${
                      row.dueDateYear
                    }`}
                  </TableCell>
                  <TableCell>
                    {row.status === ReportStatus.Upcoming && (
                      <Chip
                        label="Upcoming"
                        size="small"
                        sx={{
                          background:
                            theme.palette.mode === "dark"
                              ? theme.palette.dark.main
                              : theme.palette.info.light + 60,
                          color: theme.palette.info.dark,
                        }}
                      />
                    )}
                    {row.status === ReportStatus.Complete && (
                      <Chip
                        label="Complete"
                        size="small"
                        sx={{
                          background:
                            theme.palette.mode === "dark"
                              ? theme.palette.dark.main
                              : theme.palette.success.light + 60,
                          color: theme.palette.success.dark,
                        }}
                      />
                    )}
                    {row.status === ReportStatus.InProgress && (
                      <Chip
                        label="In Progress"
                        size="small"
                        sx={{
                          background:
                            theme.palette.mode === "dark"
                              ? theme.palette.dark.main
                              : theme.palette.warning.light,
                          color: theme.palette.warning.dark,
                        }}
                      />
                    )}
                    {row.status === ReportStatus.Overdue && (
                      <Chip
                        label="Overdue"
                        size="small"
                        sx={{
                          background:
                            theme.palette.mode === "dark"
                              ? theme.palette.dark.main
                              : theme.palette.error.light,
                          color: theme.palette.error.dark,
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ pr: 3 }}
                    onClick={() => handleRowClick(row.id)}
                  >
                    <Stack
                      direction="row"
                      justifyContent="center"
                      alignItems="center"
                    >
                      <Tooltip placement="top" title="View">
                        <IconButton
                          color="primary"
                          sx={{
                            color: theme.palette.info.dark,
                            borderColor: theme.palette.info.main,
                            "&:hover ": {
                              background: theme.palette.info.light,
                            },
                          }}
                          size="large"
                          onClick={() => handleRowClick(row.id)}
                        >
                          <RemoveRedEyeIcon sx={{ fontSize: "1.1rem" }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                  {/* <TableCell align="center" sx={{ pr: 3 }}>
                    <Stack
                      direction="row"
                      justifyContent="center"
                      alignItems="center"
                    >
                      <Tooltip placement="top" title="Message">
                        <IconButton
                          color="primary"
                          aria-label="delete"
                          size="large"
                        >
                          <ChatBubbleTwoToneIcon sx={{ fontSize: "1.1rem" }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip placement="top" title="Block">
                        <IconButton
                          color="primary"
                          sx={{
                            color: theme.palette.orange.dark,
                            borderColor: theme.palette.orange.main,
                            "&:hover ": {
                              background: theme.palette.orange.light,
                            },
                          }}
                          size="large"
                        >
                          <BlockTwoToneIcon sx={{ fontSize: "1.1rem" }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell> */}
                </TableRow>
              ))}
          </TableBody>
          {reports.length === 0 && !loading ? (
            <NoRows colSpan={4} title={"Reports"} />
          ) : null}
          {loading && <DataLoadingRow colSpan={4} />}
        </Table>
      </TableContainer>
    </StyledCard>
  );
};
